import type { ValorantRank } from 'generated/openapi';
import type { ComponentProps } from 'react';
import { memo } from 'react';

import { ValorantRankIcon } from '@/features/games/valorant/components/valorantRankIcon';
import { useGetValorantAgentById } from '@/features/games/valorant/hooks/useGetValorantAgents';
import { getAgentIconUrl } from '@/features/games/valorant/utils/agents';
import { getRankLabel } from '@/features/games/valorant/utils/rank';
import { Separator } from '@/shared/components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/shared/components/ui/tooltip';
import { cn } from '@/shared/utils/cn';

interface AgentAvatarProps {
  agentId: string;
}

const AgentAvatar = memo(function AgentAvatar({ agentId }: AgentAvatarProps) {
  const agent = useGetValorantAgentById(agentId);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <img
          src={getAgentIconUrl(agentId)}
          alt={agent?.displayName ?? ''}
          className="size-6 rounded-full border border-white/24 bg-black"
        />
      </TooltipTrigger>
      {agent && (
        <TooltipContent>
          <p>{agent.displayName}</p>
        </TooltipContent>
      )}
    </Tooltip>
  );
});

export interface ProfileOverviewCardGameInfoProps extends Omit<
  ComponentProps<'div'>,
  'children'
> {
  valorantRank: ValorantRank;
  rankedQueueRate: number | undefined;
  agentIds: string[] | undefined;
}

export const ProfileOverviewCardGameInfo = memo(
  function ProfileOverviewCardGameInfo({
    valorantRank,
    rankedQueueRate,
    agentIds,
    className,
    ...props
  }: ProfileOverviewCardGameInfoProps) {
    const hasAgents = agentIds && agentIds.length > 0;

    const playstyleInfo =
      rankedQueueRate != null
        ? (() => {
            const isCompetitive = rankedQueueRate >= 0.5;
            const percentage = Math.round(
              (isCompetitive ? rankedQueueRate : 1 - rankedQueueRate) * 100,
            );
            const label = isCompetitive ? 'Ranked' : 'Casual';
            return { percentage, label };
          })()
        : null;

    return (
      <div
        className={cn(
          'bg-titan-900 flex items-center gap-2 rounded-xl px-2 py-3',
          className,
        )}
        {...props}
      >
        <div className="flex flex-1 items-center justify-center gap-2">
          <ValorantRankIcon rank={valorantRank} className="size-6" />
          <div className="flex flex-col text-xs leading-none font-medium md:text-sm">
            <span className="text-cold-300">{getRankLabel(valorantRank)}</span>
            {playstyleInfo && (
              <span className="text-cold-500 text-[10px] md:text-xs">
                {playstyleInfo.percentage}% {playstyleInfo.label}
              </span>
            )}
          </div>
        </div>
        {hasAgents && <Separator orientation="vertical" className="h-6" />}
        {hasAgents && (
          <div className="flex flex-1 items-center justify-center gap-1">
            {agentIds.slice(0, 3).map(id => (
              <AgentAvatar key={id} agentId={id} />
            ))}
          </div>
        )}
      </div>
    );
  },
);
